<!-- src/views/Projects.vue -->
<template>
  <v-container class="py-8">
    <v-card class="pa-6" elevation="6" style="background-color: #f5f0eb">
      <v-card-title
        class="headline font-weight-bold mb-4"
        style="color: #5a3e36"
      >
        Projects
      </v-card-title>

      <v-card-text>
        <v-btn-toggle v-model="selected" mandatory class="mb-6 flex-toggle">
          <v-btn
            value="ahm"
            class="text-capitalize font-weight-bold"
            style="background-color: #d9cfc3; color: #5a3e36"
          >
            Attack Helicopter Mother
          </v-btn>

          <v-btn
            value="nister"
            class="text-capitalize font-weight-bold"
            style="background-color: #d9cfc3; color: #5a3e36"
          >
            Imagined: Yiddish Fantasies
          </v-btn>
        </v-btn-toggle>

        <v-row>
          <v-col cols="12" md="10" class="mx-auto">
            <div v-if="selected === 'ahm'">
              <h2 class="mb-4" style="color: #5a3e36">
                Attack Helicopter Mother
              </h2>
              <p>
                Transgender children have been subjected to sustained political
                attack in the United States since 2022, when Texas governor Greg
                Abbott ordered that the affirmation of their gender identities
                by loving parents be investigated as “child abuse.” Transgender
                mothers have been an object of comic mockery for more than a
                decade, since Jeffrey Tambor donned a dress (and sexually
                harassed his co-stars) in <em>Transparent</em> (2014). Is there
                space, amidst the ridicule, mendacity, and opportunism, for a
                serious re-examination of parenthood and gender by the trans
                mother of two trans children?
              </p>
              <p>
                The central thesis of
                <strong>Attack Helicopter Mother</strong> is that such space is
                needed and can be created, by (rhetorical) violence if
                necessary. Support of children’s explorations and
                transformations of their gender identities follows as an
                immediate inference from what should be axiomatic, that children
                are not the property of their parents or the state but
                autonomous persons—in the words of the book, “We do not own our
                children, but hold them in trust for their future selves.” At
                present, though, this axiom seems dangerously radical and
                countercultural. In such an environment, raising children who
                are brave enough to become themselves is not merely a matter of
                passively letting them be, but requires active defense, clearing
                out hostile, patriarchal fortifications and incursions. In this
                author’s case, that meant tearing down internal psychic walls
                that had been built in childhood and beyond, encircling her with
                a false image of safety.
              </p>
              <p>
                <strong>Attack Helicopter Mother</strong> is not a memoir.
                Instead, it is a series of feminist essays that take different
                angles of approach to disentangle the concepts of womanhood,
                femininity, and motherhood from one another. Some are based on
                recollections of events in the author’s lives and those of her
                children—though I take care to always respect my children’s
                autonomy and consent, using only those events from their lives
                and descriptions of their characters that they are comfortable
                sharing, and not mining their histories for cheap laughs or
                adoring smiles. Others engage with the growing corpus of
                transgender literature, with feminist theory, or are based on
                interviews with trans parents of widely varying experiences.
              </p>
              <p>
                <strong>Attack Helicopter Mother</strong> will be published by
                The Feminist Press in the spring of 2027 in paperback and ebook
                formats.
              </p>
            </div>

            <div v-else>
              <h2 class="mb-4" style="color: #5a3e36">
                Imagined: Yiddish Fantasies from the 1920s by Der Nister
              </h2>
              <p>
                Though his social realist work from late in his life has
                recently been translated into English--his epic novel
                <em>The Family Mashber</em> is available from NYRB Classics, and
                a posthumous collection called <em>Regrowth</em> from
                Northwestern University Press--the symbolist stories that won
                him mid-life fame among Yiddish literary circles are either
                untranslated, or scattered in out-of-print, poorly circulated
                multi-author collections. I believe if these stories were better
                known, he would be mentioned in the same breath as authors like
                Sigizmund Krzhizhanovsky, Bruno Schulz, and Franz Kafka.
              </p>
              <p>
                <strong>Imagined</strong> will consist of 13 stories published
                by Der Nister between 1922 and 1929 in Berlin and Kiev under the
                collection title <em>Gedakht</em>. I have completed full
                translations of nine of these stories. Six of those translations
                have yet to appear in any format, print or electronic. In
                addition to publication of the full collection, I also welcome
                opportunities to place Der Nister stories in magazines or
                journals.
              </p>
              <p>First world rights are available for the following stories:</p>
              <ul style="padding-left: 1.5rem">
                <li>A Fable with a Hermit and a Little Goat (9,700 words)</li>
                <li>Demons (7,300 words)</li>
                <li>In the Wine Tavern (7,500 words)</li>
                <li>The Moral (4,900 words)</li>
                <li>Beheaded (5,700 words)</li>
                <li>At the Foot of a Fence (13,200 words)</li>
              </ul>
            </div>
          </v-col>
        </v-row>
      </v-card-text>
    </v-card>
  </v-container>
</template>

<script setup>
import { ref } from "vue";

const selected = ref("ahm");
</script>

<style scoped>
/* Add some spacing at the bottom of paragraphs */
p {
  margin-bottom: 1.5rem;
}
.flex-toggle {
  display: flex;
  flex-wrap: wrap;
  justify-content: center;
  border-radius: 8px;
  overflow: hidden; /* smooth edges */
}

.flex-toggle .v-btn {
  background-color: #d9cfc3;
  color: #5a3e36;
  flex: 1 1 auto;
  min-width: 200px; /* makes wide enough even on mobile */
  border-radius: 0; /* REMOVE internal rounding */
}

@media (max-width: 960px) {
  .flex-toggle {
    flex-direction: column;
    align-items: stretch;
  }

  .flex-toggle .v-btn:first-child {
    border-top-left-radius: 4px;
    border-top-right-radius: 4px;
  }

  .flex-toggle .v-btn:last-child {
    border-bottom-left-radius: 4px;
    border-bottom-right-radius: 4px;
  }
}
</style>
